import { useConsultationContext } from '../../context/ConsultationContext';

import { PatientInfoHeader } from './PatientInfoHeader/PatientInfoHeader';
import { Stepper } from '../UI/Stepper/Stepper';
import { useNavigate } from 'react-router-dom';
import { HistoryTable } from '../UI/SelectedSalesOrder/HistoryTable';
import { DocumentList } from '../DocumentList/DocumentList';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { AppointmentDetails } from './AppointmentDetails/AppointmnetsDetails';
import { IS_CONSULTATION, STEPPER_ACTIVE_STEPS } from '../../enums/enums';
import PrimaryButtonV2 from '../UI/Buttons/PrimaryButtonV2';
import AppointmentList from './AppointmentsList/AppointmentList';
import styles from './PatientInformation.module.scss';
import backIcon from '../../assets/icons/backArrow.svg';
import { DoctorDetailsTab } from '../UI/SelectedSalesOrder/DoctorDetailsTab';
import { ConsultationTypes } from '../../enums/enums';
import { IAccountInfo, selectAccountInfo, selectUserRole } from '../../store/slices/auth';
import { NURSE_USER_ROLE } from '../../constants/userRolesConstants';
import { findProductWithTaIdAndTransactionId } from '../../helpers';
import { IDigitalHealthProfile } from '../UI/SelectedSalesOrder/DigitalHealthProfileTab';
import { useTranslation } from '../../hooks/useTranslations';

export default function PatientInformations() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translations } = useTranslation();
  const { selectedListOrder, setSelectedListOrder, workOrderStatusId } = useConsultationContext();
  const [documents, setDocuments] = useState<IDigitalHealthProfile>();
  const [assignDoctor, setAssignDoctor] = useState<boolean>(false);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const userRole: string = useAppSelector<string>(selectUserRole);

  useEffect(() => {
    if (!selectedListOrder) return;

    const fetchData = async () => {
      const data = await findProductWithTaIdAndTransactionId(selectedListOrder);
      setDocuments(data);
    };

    fetchData();
    setAssignDoctor(false);
  }, [dispatch, selectedListOrder]);

  const documentsDisplayed = useMemo(() => {
    return documents?.attachments;
  }, [documents]);

  const isDiagnostic =
    selectedListOrder?.service_service_group.service_group_id === 1 ||
    selectedListOrder?.service_service_group.service_group_id === 4;

  const isNurseInClinik =
    userRole === NURSE_USER_ROLE &&
    selectedListOrder?.service?.service_id === ConsultationTypes.IN_CLINIK_CONSULTATION &&
    (selectedListOrder?.work_order?.work_order_status_id === 5 ||
      selectedListOrder?.work_order?.work_order_status_id === 6);

  const handleNavigation = async () => {
    if (isDiagnostic) {
      navigate(
        `/web/schedule?selectedTab=check-in&accountNumber=${selectedListOrder.customer_person.person_account_number}&salesOrderId=${selectedListOrder.sales_order.sales_order_id}&salesOrderItemId=${selectedListOrder?.sales_order_item?.sales_order_item_id}`
      );
      setSelectedListOrder(null);
      return;
    }
    if (isNurseInClinik) {
      navigate('/web/prejoin-consultation', {
        state: {
          ...selectedListOrder,
          person: selectedListOrder?.customer_person,
          sales_order: selectedListOrder?.sales_order,
          sales_order_item: selectedListOrder?.sales_order_item,
          sales_order_appointment: selectedListOrder?.sales_order_appointment,
          service: selectedListOrder?.service,
          work_order: {
            ...selectedListOrder?.work_order,
            work_order_id: workOrderStatusId,
          },
        },
      });
      return;
    }
    if (selectedListOrder)
      navigate(
        `/web/consultation/${selectedListOrder.sales_order_item.sales_order_id}/${selectedListOrder.sales_order_item.sales_order_item_id}`
      );
    setSelectedListOrder(null);
    return;
  };

  const handleConsultationButtonLabel = () => {
    if (selectedListOrder?.work_order?.work_order_status_id === 1) {
      return accountInfo.consultation_with_extra_steps
        ? translations('consultation.prepared')
        : selectedListOrder?.service_service_group.service_group_id ===
            ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
          selectedListOrder?.service_service_group.service_group_id ===
            ConsultationTypes.REVIEW_CONSULTATION
        ? translations('start.consultation')
        : translations('start.video.consultation');
    }
    if (selectedListOrder?.work_order?.work_order_status_id === 4) {
      return translations('finalize.consultation');
    }
    if (selectedListOrder?.work_order?.work_order_status_id === 6) {
      return isNurseInClinik
        ? translations('button.join_video_call')
        : translations('park.consultation');
    }
    if (
      userRole === NURSE_USER_ROLE &&
      (selectedListOrder?.work_order?.work_order_status_id === 5 ||
        selectedListOrder?.work_order?.work_order_status_id === 5)
    ) {
      return translations('button.join_video_call');
    }
    if (selectedListOrder?.work_order?.work_order_status_id === 5) {
      if (
        selectedListOrder?.service_service_group.service_group_id ===
          ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
        selectedListOrder?.service_service_group.service_group_id ===
          ConsultationTypes.REVIEW_CONSULTATION
      ) {
        return translations('start.consultation');
      } else {
        return translations('start.video.consultation');
      }
    }
    if (selectedListOrder?.work_order?.work_order_status_id === 2) {
      return 'Close';
    }
    return accountInfo.consultation_with_extra_steps
      ? translations('prepare.consultation')
      : selectedListOrder?.service_service_group.service_group_id ===
          ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
        selectedListOrder?.service_service_group.service_group_id ===
          ConsultationTypes.REVIEW_CONSULTATION
      ? translations('start.consultation')
      : translations('start.video.consultation');
  };

  return (
    <div className={styles.mainContainer}>
      <AppointmentList />
      <div className={styles.container}>
        <PatientInfoHeader selectedListOrder={selectedListOrder} />
        {selectedListOrder && (
          <div className={styles.stepperContainer}>
            {IS_CONSULTATION.includes(
              selectedListOrder?.service_service_group?.service_group_id &&
                accountInfo.consultation_with_extra_steps
            ) && (
              <Stepper
                steps={[
                  { displayText: translations('open') },
                  { displayText: translations('prepare') },
                  { displayText: translations('prepared') },
                  { displayText: translations('started') },
                  { displayText: translations('parked') },
                  { displayText: translations('finalized') },
                ]}
                activeStep={
                  STEPPER_ACTIVE_STEPS[selectedListOrder?.work_order?.work_order_status_id] || 0
                }
              />
            )}
            {selectedListOrder?.work_order?.work_order_status_id !== 2 && (
              <PrimaryButtonV2
                text={isDiagnostic ? translations('check.in') : handleConsultationButtonLabel()}
                onClick={handleNavigation}
              />
            )}
          </div>
        )}
        {selectedListOrder ? (
          <div className={styles.selectedOrderContainer}>
            <div className={styles.headerContainer}>
              <p className={styles.headerNames}>{translations('appointment.details')}</p>
              {assignDoctor && (
                <div className={styles.backContainer} onClick={() => setAssignDoctor(false)}>
                  <img src={backIcon} alt="back" />
                  {translations('back')}
                </div>
              )}
            </div>
            <div className={styles.divider} />
            {assignDoctor ? (
              <DoctorDetailsTab selectedSalesOrder={selectedListOrder} />
            ) : (
              <div>
                <AppointmentDetails
                  setAssignDoctor={setAssignDoctor}
                  selectedListOrder={selectedListOrder}
                />

                {documentsDisplayed && (
                  <>
                    <p className={styles.headerNames}>{translations('documents')}</p>
                    <DocumentList
                      userInfo={selectedListOrder?.customer_person}
                      documents={documentsDisplayed}
                    />
                  </>
                )}
                <div className={styles.divider} />
                <p className={styles.headerNames}>{translations('appointment.history')}</p>
                <HistoryTable customer_person_id={selectedListOrder?.customer_person.person_id} />
              </div>
            )}
          </div>
        ) : (
          <div className={styles.selectedOrderContainer} style={{ padding: '60px' }}>
            <div className={styles.emptyStateContainer}>No Order Selected</div>
          </div>
        )}
      </div>
    </div>
  );
}
